import React, { useState, useContext } from "react";
import { Helmet } from "react-helmet";
import { Icon } from "@iconify/react";
import { toast } from "sonner";
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Skeleton,
  Stack,
  Divider,
  Tooltip,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { AuthContext } from "../../context/AuthContextProvider";
import {
  useGetFacilities,
  createFacility,
  updateFacility,
  deleteFacility,
} from "../../api/facilities";
import CentreSelect from "../../components/CentreSelect";
import { BlankPreview } from "../../components/BlankPreview";
import nopreview from "../../assets/images/others/no-preview.png";

const FACILITY_TYPES = ["ambulance"]; // ['ambulance', 'icu', 'pharmacy', 'lab', 'other'];
const STATUS_OPTIONS = ["available", "busy", "offline", "maintenance"];

const STATUS_STYLE = {
  available: { bg: "#DAF4E1", color: "#348530", label: "Available" },
  busy: { bg: "#FFF3CD", color: "#E4841A", label: "Busy" },
  offline: { bg: "#F0F0F0", color: "var(--app-text-muted)", label: "Offline" },
  maintenance: { bg: "#FFEBE8", color: "#D32F2F", label: "Maintenance" },
};

const FONT = "Figtree, sans-serif";

const EMPTY_FORM = {
  facility_type: "ambulance",
  name: "",
  contactPerson: "",
  phone_number: "",
  vehicleNumber: "",
  driverName: "",
  status: "available",
  notes: "",
  centerId: "",
  location: { type: "Point", coordinates: [0, 0] },
  gpsProvider: {
    name: "",
    apiKey: "",
    apiSecret: "",
    deviceId: "",
    trackingUrl: "",
    webhookUrl: "",
  },
};

function StatusChip({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.offline;
  return (
    <Chip
      label={s.label}
      size="small"
      sx={{
        fontFamily: FONT,
        fontWeight: 600,
        fontSize: "12px",
        borderRadius: "6px",
        backgroundColor: s.bg,
        color: s.color,
      }}
    />
  );
}

function FacilityFormDialog({
  open,
  onClose,
  initial,
  onSaved,
  defaultCenterId,
}) {
  const hasGpsProvider = !!initial?.gpsProvider?.name;
  const [useGps, setUseGps] = useState(hasGpsProvider);

  const [form, setForm] = useState(() => ({
    ...EMPTY_FORM,
    ...(initial || {}),
    centerId: initial?.centerId || defaultCenterId || "",
    location: initial?.location || { type: "Point", coordinates: [0, 0] },
    gpsProvider: { ...EMPTY_FORM.gpsProvider, ...(initial?.gpsProvider || {}) },
  }));
  const [saving, setSaving] = useState(false);
  const [detecting, setDetecting] = useState(false);

  const handleDetectLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation not supported by your browser");
      return;
    }
    setDetecting(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = parseFloat(pos.coords.latitude.toFixed(6));
        const lng = parseFloat(pos.coords.longitude.toFixed(6));
        setForm((f) => ({
          ...f,
          location: { type: "Point", coordinates: [lng, lat] },
        }));
        setDetecting(false);
        toast.success("Location detected");
      },
      () => {
        toast.error("Unable to detect location. Please allow location access.");
        setDetecting(false);
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  const isEdit = !!initial?._id;

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));
  const setGps = (key, val) =>
    setForm((f) => ({ ...f, gpsProvider: { ...f.gpsProvider, [key]: val } }));

  const setCoord = (axis, val) => {
    const num = parseFloat(val) || 0;
    setForm((f) => {
      const coords = [...(f.location?.coordinates || [0, 0])];
      if (axis === "lng") coords[0] = num;
      else coords[1] = num;
      return { ...f, location: { type: "Point", coordinates: coords } };
    });
  };

  const handleSave = async () => {
    if (!form.centerId) {
      toast.error("Please select a centre");
      return;
    }
    if (!form.name) {
      toast.error("Facility name is required");
      return;
    }
    if (!form.phone_number) {
      toast.error("Phone number is required");
      return;
    }

    const payload = {
      ...form,
      gpsProvider: useGps ? form.gpsProvider : {},
      location: useGps ? { type: "Point", coordinates: [0, 0] } : form.location,
    };

    setSaving(true);
    const res = isEdit
      ? await updateFacility(initial._id, payload)
      : await createFacility(payload);
    setSaving(false);

    if (res?.success) {
      toast.success(isEdit ? "Facility updated" : "Facility added");
      onSaved();
      onClose();
    } else {
      toast.error(res?.message || "Something went wrong");
    }
  };

  const field = (label, key, type = "text", required = false) => (
    <TextField
      label={label}
      value={form[key] || ""}
      onChange={(e) => set(key, e.target.value)}
      size="small"
      fullWidth
      required={required}
      type={type}
      InputProps={{ style: { fontFamily: FONT } }}
      InputLabelProps={{ style: { fontFamily: FONT } }}
    />
  );

  const gpsField = (label, key) => (
    <TextField
      label={label}
      value={form.gpsProvider?.[key] || ""}
      onChange={(e) => setGps(key, e.target.value)}
      size="small"
      fullWidth
      InputProps={{ style: { fontFamily: FONT } }}
      InputLabelProps={{ style: { fontFamily: FONT } }}
    />
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontFamily: FONT, fontWeight: 700, pb: 1 }}>
        {isEdit ? "Edit Facility" : "Add Facility"}
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2 }}>
        <Stack spacing={2.5}>
          {/* Centre selection */}
          <Box>
            <Typography
              sx={{
                fontFamily: FONT,
                fontSize: "12px",
                fontWeight: 600,
                color: "var(--app-text)",
                mb: 0.5,
              }}
            >
              Centre <span style={{ color: "#d32f2f" }}>*</span>
            </Typography>
            <CentreSelect
              valueId={form.centerId}
              onChange={(opt) => set("centerId", opt?.value || "")}
              placeholder="Select Centre"
            />
          </Box>

          <Stack direction="row" spacing={2}>
            <FormControl size="small" fullWidth>
              <InputLabel sx={{ fontFamily: FONT }}>Facility Type</InputLabel>
              <Select
                value={form.facility_type}
                label="Facility Type"
                onChange={(e) => set("facility_type", e.target.value)}
                sx={{ fontFamily: FONT }}
              >
                {FACILITY_TYPES.map((t) => (
                  <MenuItem
                    key={t}
                    value={t}
                    sx={{ fontFamily: FONT, textTransform: "capitalize" }}
                  >
                    {t.charAt(0).toUpperCase() + t.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel sx={{ fontFamily: FONT }}>Status</InputLabel>
              <Select
                value={form.status}
                label="Status"
                onChange={(e) => set("status", e.target.value)}
                sx={{ fontFamily: FONT }}
              >
                {STATUS_OPTIONS.map((s) => (
                  <MenuItem
                    key={s}
                    value={s}
                    sx={{ fontFamily: FONT, textTransform: "capitalize" }}
                  >
                    {s.charAt(0).toUpperCase() + s.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          {field("Facility Name *", "name", "text", true)}

          <Stack direction="row" spacing={2}>
            {field("Contact Person", "contactPerson")}
            {field("Phone Number *", "phone_number", "text", true)}
          </Stack>

          {form.facility_type === "ambulance" && (
            <Stack direction="row" spacing={2}>
              {field("Driver Name", "driverName")}
              {field("Vehicle Number", "vehicleNumber")}
            </Stack>
          )}

          {field("Notes", "notes")}

          {/* GPS toggle section */}
          <Box
            sx={{
              border: "1px solid var(--app-border)",
              borderRadius: "10px",
              p: 2,
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <Icon
                  icon={
                    useGps ? "mdi:satellite-uplink" : "mdi:map-marker-outline"
                  }
                  width={20}
                  color="#5751E9"
                />
                <Typography
                  sx={{
                    fontFamily: FONT,
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "var(--app-text)",
                  }}
                >
                  {useGps
                    ? "GPS / Tracking Integration"
                    : "Manual Location (Lat / Long)"}
                </Typography>
              </Stack>
              <FormControlLabel
                control={
                  <Switch
                    checked={useGps}
                    onChange={(e) => setUseGps(e.target.checked)}
                    size="small"
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": {
                        color: "#FFA72B",
                      },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                        { backgroundColor: "#FFA72B" },
                    }}
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontFamily: FONT,
                      fontSize: "12px",
                      color: "var(--app-text-muted)",
                    }}
                  >
                    {useGps ? "GPS On" : "GPS Off"}
                  </Typography>
                }
                labelPlacement="start"
                sx={{ mr: 0, ml: 0 }}
              />
            </Stack>

            <Box sx={{ mt: 2 }}>
              {useGps ? (
                <Stack spacing={2}>
                  <Stack direction="row" spacing={2}>
                    {gpsField("GPS Provider Name", "name")}
                    {gpsField("Device ID", "deviceId")}
                  </Stack>
                  <Stack direction="row" spacing={2}>
                    {gpsField("API Key", "apiKey")}
                    {gpsField("API Secret", "apiSecret")}
                  </Stack>
                  {gpsField("Tracking URL", "trackingUrl")}
                  {gpsField("Webhook URL", "webhookUrl")}
                </Stack>
              ) : (
                <Stack spacing={1.5}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleDetectLocation}
                    disabled={detecting}
                    startIcon={
                      detecting ? (
                        <CircularProgress size={14} color="inherit" />
                      ) : (
                        <Icon icon="mdi:crosshairs-gps" width={16} />
                      )
                    }
                    sx={{
                      fontFamily: FONT,
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: "13px",
                      borderRadius: "8px",
                      borderColor: "#5751E9",
                      color: "#5751E9",
                      "&:hover": {
                        backgroundColor: "#F0EFFF",
                        borderColor: "#5751E9",
                      },
                      alignSelf: "flex-start",
                    }}
                  >
                    {detecting ? "Detecting..." : "Use Current Location"}
                  </Button>
                  <Stack direction="row" spacing={2}>
                    <TextField
                      label="Latitude"
                      size="small"
                      fullWidth
                      type="number"
                      value={form.location?.coordinates?.[1] || ""}
                      onChange={(e) => setCoord("lat", e.target.value)}
                      placeholder="e.g. 28.6139"
                      InputProps={{ style: { fontFamily: FONT } }}
                      InputLabelProps={{ style: { fontFamily: FONT } }}
                    />
                    <TextField
                      label="Longitude"
                      size="small"
                      fullWidth
                      type="number"
                      value={form.location?.coordinates?.[0] || ""}
                      onChange={(e) => setCoord("lng", e.target.value)}
                      placeholder="e.g. 77.2090"
                      InputProps={{ style: { fontFamily: FONT } }}
                      InputLabelProps={{ style: { fontFamily: FONT } }}
                    />
                  </Stack>
                </Stack>
              )}
            </Box>
          </Box>
        </Stack>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button
          onClick={onClose}
          sx={{
            fontFamily: FONT,
            textTransform: "none",
            color: "var(--app-text-muted)",
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          startIcon={
            saving ? <CircularProgress size={14} color="inherit" /> : null
          }
          sx={{
            fontFamily: FONT,
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "8px",
            backgroundColor: "#2563EB",
            "&:hover": { backgroundColor: "#1d4ed8" },
          }}
        >
          {saving ? "Saving..." : isEdit ? "Update" : "Add Facility"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const TABLE_HEADERS = [
  "NAME",
  "TYPE",
  "DRIVER / CONTACT",
  "PHONE",
  "VEHICLE",
  "STATUS",
  "GPS",
  "ACTIONS",
];

export default function ManageFacilities() {
  const { user } = useContext(AuthContext);

  const [selectedCentreId, setSelectedCentreId] = useState(
    user?.selectedCenter || "",
  );
  const [args, setArgs] = useState({
    facility_type: "ambulance",
    centerId: user?.selectedCenter || "",
  });

  const { facilities, isLoading, mutate } = useGetFacilities(
    args.centerId ? args : { facility_type: args.facility_type },
  );

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedFacility, setSelectedFacility] = useState(null);

  const handleCentreChange = (opt) => {
    const id = opt?.value || "";
    setSelectedCentreId(id);
    setArgs((a) => ({ ...a, centerId: id }));
  };

  const handleEdit = (f) => {
    setEditTarget(f);
    setDialogOpen(true);
  };
  const handleAdd = () => {
    setEditTarget(null);
    setDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedFacility) return;

    const res = await deleteFacility(selectedFacility._id);

    if (res?.success) {
      toast.success("Facility deleted");
      mutate();
    } else {
      toast.error(res?.message || "Delete failed");
    }

    setOpenDeleteDialog(false);
    setSelectedFacility(null);
  };

  return (
    <Box className="main-wrappers">
      <Box className="page-wrapper" sx={{ pb: "24px", fontFamily: FONT }}>
        <Box className="content">
          <Helmet>
            <title>Manage Facilities | Spandan One</title>
          </Helmet>

          {/* Header */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            mb={3}
            flexWrap="wrap"
            gap={2}
          >
            <Box>
              <Typography
                sx={{
                  fontFamily: FONT,
                  fontWeight: 700,
                  fontSize: "22px",
                  color: "var(--app-text)",
                }}
              >
                Manage Facilities
              </Typography>
              <Typography
                sx={{
                  fontFamily: FONT,
                  fontSize: "13px",
                  color: "var(--app-text-muted)",
                  mt: 0.3,
                }}
              >
                Add and manage ambulances, GPS integrations, and other facility
                details
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<Icon icon="mdi:plus" />}
              onClick={handleAdd}
              sx={{
                fontFamily: FONT,
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "8px",
                backgroundColor: "#2563EB",
                "&:hover": { backgroundColor: "#1d4ed8" },
              }}
            >
              Add Facility
            </Button>
          </Stack>

          {/* Centre filter */}
          <Box sx={{ mb: 2.5, maxWidth: 320 }}>
            <Typography
              sx={{
                fontFamily: FONT,
                fontSize: "12px",
                fontWeight: 600,
                color: "var(--app-text)",
                mb: 0.5,
              }}
            >
              Filter by Centre
            </Typography>
            <CentreSelect
              valueId={selectedCentreId}
              onChange={handleCentreChange}
              placeholder="All Centres"
            />
          </Box>

          {/* Table */}
          <TableContainer className="app-table-card"
            component={Paper}
            elevation={0}
            sx={{ backgroundColor: "transparent", boxShadow: "none" }}
          >
            <Table
              size="small"
              sx={{
                borderCollapse: "separate",
                borderSpacing: "0 10px",
                backgroundColor: "var(--app-surface-2)",
                "& .MuiTableCell-root": { borderBottom: "none" },
              }}
            >
              <TableHead>
                <TableRow sx={{ backgroundColor: "transparent" }}>
                  {TABLE_HEADERS.map((h) => (
                    <TableCell
                      key={h}
                      sx={{
                        fontFamily: FONT,
                        fontWeight: 800,
                        fontSize: "13px",
                        color: "#A3A3A3",
                        letterSpacing: "0.5px",
                        py: 1.5,
                        whiteSpace: "nowrap",
                        backgroundColor: "var(--app-surface-2)",
                        borderBottom: "none",
                      }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  Array.from({ length: 4 }).map((_, i) => (
                    <TableRow key={i}>
                      {TABLE_HEADERS.map((h) => (
                        <TableCell key={h}>
                          <Skeleton variant="text" width="80%" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : facilities.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={TABLE_HEADERS.length}
                      sx={{ textAlign: "center", py: 6 }}
                    >
                      <BlankPreview
                        icon={nopreview}
                        title="No facilities found"
                        subtitle="Add your first facility using the button above"
                      />
                    </TableCell>
                  </TableRow>
                ) : (
                  facilities.map((f) => (
                    <TableRow
                      key={f._id}
                      sx={{
                        backgroundColor: "var(--app-surface)",
                        borderRadius: "8px",
                        "& td": { py: "10px", fontSize: "13px" },
                        "& td:first-of-type": {
                          borderTopLeftRadius: "8px",
                          borderBottomLeftRadius: "8px",
                        },
                        "& td:last-of-type": {
                          borderTopRightRadius: "8px",
                          borderBottomRightRadius: "8px",
                        },
                      }}
                    >
                      <TableCell
                        sx={{
                          fontFamily: FONT,
                          fontWeight: 600,
                          fontSize: "13px",
                        }}
                      >
                        {f.name || "—"}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={f.facility_type || "ambulance"}
                          size="small"
                          sx={{
                            fontFamily: FONT,
                            fontSize: "11px",
                            fontWeight: 600,
                            textTransform: "capitalize",
                            borderRadius: "6px",
                            backgroundColor: "#EFF6FF",
                            color: "#2563EB",
                          }}
                        />
                      </TableCell>
                      <TableCell
                        sx={{
                          fontFamily: FONT,
                          fontSize: "13px",
                          color: "var(--app-text)",
                        }}
                      >
                        {f.driverName || f.contactPerson || "—"}
                      </TableCell>
                      <TableCell>
                        {f.phone_number ? (
                          <a
                            href={`tel:${f.phone_number}`}
                            style={{
                              color: "#2563EB",
                              fontFamily: FONT,
                              fontSize: "13px",
                              textDecoration: "none",
                              fontWeight: 600,
                            }}
                          >
                            {f.phone_number}
                          </a>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontFamily: FONT,
                          fontSize: "13px",
                          color: "var(--app-text)",
                        }}
                      >
                        {f.vehicleNumber || "—"}
                      </TableCell>
                      <TableCell>
                        <StatusChip status={f.status} />
                      </TableCell>
                      <TableCell>
                        {f.gpsProvider?.name ? (
                          <Tooltip
                            title={`Provider: ${f.gpsProvider.name}${f.gpsProvider.deviceId ? ` | Device: ${f.gpsProvider.deviceId}` : ""}`}
                          >
                            <Chip
                              label={f.gpsProvider.name}
                              size="small"
                              icon={
                                <Icon icon="mdi:satellite-uplink" width={14} />
                              }
                              sx={{
                                fontFamily: FONT,
                                fontSize: "11px",
                                fontWeight: 600,
                                borderRadius: "6px",
                                backgroundColor: "#F0FDF4",
                                color: "#348530",
                              }}
                            />
                          </Tooltip>
                        ) : (
                          <Typography
                            sx={{
                              fontFamily: FONT,
                              fontSize: "12px",
                              color: "#bbb",
                            }}
                          >
                            Not set
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5}>
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(f)}
                              sx={{ color: "#2563EB" }}
                            >
                              <Icon icon="mdi:pencil-outline" width={18} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSelectedFacility(f);
                                setOpenDeleteDialog(true);
                              }}
                              sx={{ color: "#D32F2F" }}
                            >
                              <Icon icon="mdi:trash-can-outline" width={18} />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {dialogOpen && (
            <FacilityFormDialog
              open={dialogOpen}
              onClose={() => {
                setDialogOpen(false);
                setEditTarget(null);
              }}
              initial={editTarget}
              defaultCenterId={selectedCentreId}
              onSaved={() => mutate()}
            />
          )}
          <Dialog
            open={openDeleteDialog}
            onClose={() => setOpenDeleteDialog(false)}
            aria-labelledby="delete-dialog-title"
            PaperProps={{
              sx: {
                borderRadius: "20px",
                padding: "0px",
                width: "508px",
                boxShadow: "var(--app-shadow)",
                position: "relative",
              },
            }}
          >
            {/* Close Icon */}
            <Icon
              icon="mdi:close"
              width="26"
              height="26"
              onClick={() => setOpenDeleteDialog(false)}
              style={{
                position: "absolute",
                right: 16,
                top: 16,
                cursor: "pointer",
                color: "#444",
              }}
            />

            {/* --------- TITLE + ICON --------- */}
            <DialogTitle
              id="delete-dialog-title"
              sx={{
                textAlign: "center",
                fontWeight: "700",
                fontSize: "22px",
                mb: 1,
                mt: 2,
              }}
            >
              <Box display="flex" flexDirection="column" alignItems="center">
                <Icon
                  icon="mdi:alert"
                  width="65"
                  height="65"
                  style={{ color: "#E04A3A", marginBottom: "12px" }}
                />
                Delete Facility?
              </Box>
            </DialogTitle>

            {/* --------- BODY TEXT --------- */}
            <DialogContent sx={{ px: 4 }}>
              <Typography
                align="center"
                variant="body1"
                sx={{
                  color: "var(--app-text-muted)",
                  fontSize: "16px",
                  lineHeight: "1.6",
                  mt: 1,
                }}
              >
                Are you sure you want to delete{" "}
                <strong>{selectedFacility?.name}</strong>?
              </Typography>
            </DialogContent>

            {/* --------- ACTION BUTTONS --------- */}
            <DialogActions
              className="spn-model-btn"
              sx={{
                justifyContent: "center",
                mt: 1,
                gap: 2,
                pb: 2,
              }}
            >
              {/* Left Button – Light Red Like Your Ref UI */}
              <Button
                className="btn btn-added-danger"
                sx={{ boxShadow: "none" }}
                variant="contained"
                size="medium"
                onClick={handleDelete}
              >
                Yes, Delete
              </Button>

              {/* Right Button – Purple */}
              <Button
                className="btn btn-added-primary"
                sx={{ boxShadow: "none" }}
                variant="contained"
                size="medium"
                onClick={() => {
                  setOpenDeleteDialog(false);
                  setSelectedFacility(null);
                }}
              >
                No, Go Back
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Box>
    </Box>
  );
}
